import React, { useState } from 'react';
import { Comment, CommentVisibility } from '../../types/conditions';
import { Send, User, Globe, Lock } from 'lucide-react';
interface CommentsThreadProps {
  comments: Comment[];
}
export function CommentsThread({
  comments
}: CommentsThreadProps) {
  const [localNewComments, setLocalNewComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [visibility, setVisibility] = useState<CommentVisibility>('Public');
  // Render canonical thread from props (so unread/read updates from the
  // parent flow through) plus any comments composed locally this session.
  const displayedComments = [...comments, ...localNewComments];
  const handleSend = () => {
    if (!newComment.trim()) return;
    const comment: Comment = {
      id: `CM-NEW-${Date.now()}`,
      authorName: 'Current User',
      authorRole: 'Loan Officer',
      timestamp: new Date().toISOString(),
      message: newComment.trim(),
      visibility,
      isRead: true
    };
    setLocalNewComments(prev => [...prev, comment]);
    setNewComment('');
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const isPrivate = visibility === 'Private';
  return <div className="flex flex-col h-full" data-id="element-1">
      <div className="flex-1 space-y-4 mb-4 max-h-80 overflow-y-auto pr-2" data-id="element-2">
        {displayedComments.length === 0 ? <p className="text-sm text-slate-500 italic text-center py-4" data-id="element-3">
            No comments yet.
          </p> : displayedComments.map(comment => {
        const priv = comment.visibility === 'Private';
        const unread = comment.isRead === false;
        return <div key={comment.id} className="flex space-x-3" data-id="element-4">
                <div className="flex-shrink-0 relative" data-id="element-5">
                  <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500" data-id="element-6">
                    <User className="h-5 w-5" data-id="element-7" />
                  </div>
                  {unread && <span className="absolute -top-0.5 -right-0.5 block h-2.5 w-2.5 rounded-full bg-[#0b5cab] ring-2 ring-white" aria-label="Unread" data-id="element-8" />}
                </div>
                <div className="flex-1 space-y-1 min-w-0" data-id="element-9">
                  <div className="flex items-center justify-between gap-2" data-id="element-10">
                    <h3 className="text-sm font-medium text-slate-900 truncate flex items-center" data-id="element-11">
                      {comment.authorName}
                      <span className="text-xs font-normal text-slate-500 ml-1" data-id="element-12">
                        ({comment.authorRole})
                      </span>
                      {unread && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-[#0b5cab] text-white" data-id="element-13">
                          New
                        </span>}
                    </h3>
                    <p className="text-xs text-slate-500 flex-shrink-0" data-id="element-14">
                      {new Date(comment.timestamp).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </p>
                  </div>
                  <div className={`text-sm p-3 rounded-lg border ${priv ? 'bg-amber-50 border-amber-200 text-amber-950' : unread ? 'bg-blue-50 border-blue-200 text-slate-900' : 'bg-slate-50 border-slate-100 text-slate-700'}`} data-id="element-15">
                    {priv && <div className="flex items-center text-[11px] font-semibold uppercase tracking-wide text-amber-700 mb-1" data-id="element-16">
                        <Lock className="h-3 w-3 mr-1" data-id="element-17" />
                        Internal Only
                      </div>}
                    <p className="whitespace-pre-wrap" data-id="element-18">{comment.message}</p>
                  </div>
                </div>
              </div>;
      })}
      </div>

      <div className="mt-auto" data-id="element-19">
        <div className="flex items-center mb-2 bg-slate-100 rounded-md p-0.5 w-fit" data-id="element-20">
          <button type="button" onClick={() => setVisibility('Public')} className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium transition-colors ${!isPrivate ? 'bg-white text-[#0b5cab] shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} aria-pressed={!isPrivate} data-id="element-21">
            <Globe className="h-3.5 w-3.5 mr-1" data-id="element-22" />
            Public
          </button>
          <button type="button" onClick={() => setVisibility('Private')} className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium transition-colors ${isPrivate ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} aria-pressed={isPrivate} data-id="element-23">
            <Lock className="h-3.5 w-3.5 mr-1" data-id="element-24" />
            Private
          </button>
        </div>

        <p className="text-[11px] text-slate-500 mb-2" data-id="element-25">
          {isPrivate ? <>
              <span className="font-medium text-amber-700" data-id="element-26">Internal only.</span>{' '}
              The borrower will not see this comment.
            </> : <>
              <span className="font-medium text-[#0b5cab]" data-id="element-27">Public.</span> The
              borrower will see this comment.
            </>}
        </p>

        <div className="relative" data-id="element-28">
          <textarea rows={2} className={`block w-full rounded-md border shadow-sm sm:text-sm p-3 pr-12 resize-none transition-colors ${isPrivate ? 'border-amber-300 bg-amber-50/40 focus:border-amber-500 focus:ring-amber-500' : 'border-slate-300 focus:border-[#0b5cab] focus:ring-[#0b5cab]'}`} placeholder={isPrivate ? 'Add an internal note... (Press Enter to send)' : 'Type a reply... (Press Enter to send)'} value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={handleKeyDown} data-id="element-29" />
          <button onClick={handleSend} disabled={!newComment.trim()} className={`absolute bottom-2 right-2 p-2 rounded-md text-white disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors ${isPrivate ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#0b5cab] hover:bg-[#094a89]'}`} aria-label={isPrivate ? 'Send private comment' : 'Send public comment'} data-id="element-30">
            <Send className="h-4 w-4" data-id="element-31" />
          </button>
        </div>
      </div>
    </div>;
}